import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import { formatarCNPJ } from '@spark/utils/formatters';
import {
  getDepositoClienteById,
  createDepositoCliente,
  updateDepositoCliente,
  deleteDepositoCliente,
  getNomeClientePorCnpj
} from '@spark/services/deposito';
import { depositoClienteFilterConfig } from '../config/depositoClienteFilterConfig';
import { getDepositoClienteColumns } from '../config/depositoClienteColumns';
import {
  emptyDepositoClienteForm,
  depositoClienteFormSections,
  depositoClienteFormStructure,
  validateDepositoClienteForm
} from '../config/depositoClienteFormConfig';
import { useDepositoClientes } from '../hooks/useDepositoClientes';

const toFormValues = (data) => ({
  id: data?.id ?? '',
  nomeCliente: data?.nomeCliente || '',
  cnpjCliente: formatarCNPJ(data?.cnpjCliente || ''),
  diasRecorrencia: data?.diasRecorrencia ?? '',
  diaEmissaoInicial: data?.diaEmissaoInicial ?? '',
  diaEmissaoFinal: data?.diaEmissaoFinal ?? '',
  tokenRastreamento: data?.tokenRastreamento || '',
  tokenRastreamentoAtivo: data?.tokenRastreamentoAtivo ?? false,
  nomeResponsavelComercial: data?.nomeResponsavelComercial || '',
  nomeResponsavelLancamento: data?.nomeResponsavelLancamento || '',
  nomeResponsavelFechamento: data?.nomeResponsavelFechamento || '',
  templateCabecalhoPaletizacao: data?.templateCabecalhoPaletizacao || '',
  templateItensPaletizacao: data?.templateItensPaletizacao || '',
  templateCabecalhoEtiquetagem: data?.templateCabecalhoEtiquetagem || '',
  templateItensEtiquetagem: data?.templateItensEtiquetagem || '',
  templateCabecalhoArmazenagem: data?.templateCabecalhoArmazenagem || '',
  templateItensArmazenagem: data?.templateItensArmazenagem || '',
  observacoesEmissao: data?.observacoesEmissao || ''
});

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDigitsOnly = (value) => (value ? String(value).replace(/\D/g, '') : undefined);

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildPayload = (values) => {
  const payload = {
    NomeCliente: normalizeText(values.nomeCliente),
    CNPJCliente: toDigitsOnly(values.cnpjCliente),
    DiasRecorrencia: parseNumber(values.diasRecorrencia),
    DiaEmissaoInicial: parseNumber(values.diaEmissaoInicial),
    DiaEmissaoFinal: parseNumber(values.diaEmissaoFinal),
    TokenRastreamento: normalizeText(values.tokenRastreamento),
    TokenRastreamentoAtivo: values.tokenRastreamentoAtivo === true,
    NomeResponsavelComercial: normalizeText(values.nomeResponsavelComercial),
    NomeResponsavelLancamento: normalizeText(values.nomeResponsavelLancamento),
    NomeResponsavelFechamento: normalizeText(values.nomeResponsavelFechamento),
    TemplateCabecalhoPaletizacao: normalizeText(values.templateCabecalhoPaletizacao),
    TemplateItensPaletizacao: normalizeText(values.templateItensPaletizacao),
    TemplateCabecalhoEtiquetagem: normalizeText(values.templateCabecalhoEtiquetagem),
    TemplateItensEtiquetagem: normalizeText(values.templateItensEtiquetagem),
    TemplateCabecalhoArmazenagem: normalizeText(values.templateCabecalhoArmazenagem),
    TemplateItensArmazenagem: normalizeText(values.templateItensArmazenagem),
    ObservacoesEmissao: normalizeText(values.observacoesEmissao)
  };

  const filtered = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

  if (values.tokenRastreamento !== undefined && values.tokenRastreamento !== null && String(values.tokenRastreamento).trim() === '') {
    filtered.TokenRastreamento = null;
  }

  [
    'TemplateCabecalhoPaletizacao',
    'TemplateItensPaletizacao',
    'TemplateCabecalhoEtiquetagem',
    'TemplateItensEtiquetagem',
    'TemplateCabecalhoArmazenagem',
    'TemplateItensArmazenagem',
    'ObservacoesEmissao'
  ].forEach((field) => {
    const rawValue = values[field.charAt(0).toLowerCase() + field.slice(1)];
    if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() === '') {
      filtered[field] = '';
    }
  });

  return filtered;
};

const generateTrackingToken = () => {
  try {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    const base64 = btoa(String.fromCharCode(...bytes));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (error) {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 18)}-${Math.random().toString(36).slice(2, 18)}`;
  }
};

const DepositoClientesModal = ({ show, onHide }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyDepositoClienteForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingRemoveId, setLoadingRemoveId] = useState(null);
  const formRef = useRef(null);
  const nomeClienteLookupRef = useRef('');
  const {
    clientes,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    resetFiltros,
    reload
  } = useDepositoClientes(show);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyDepositoClienteForm);
      setLoadingEditId(null);
      setLoadingRemoveId(null);
      nomeClienteLookupRef.current = '';
      resetFiltros();
    }
  }, [show, resetFiltros]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getDepositoClienteById(id);
      if (!data) {
        notification.warning('Cliente nao encontrado.');
        return;
      }
      setEditingId(id);
      setFormInitialValues(toFormValues(data));
    } catch (error) {
      notification.error('Erro ao carregar cliente');
      console.error('Erro ao carregar cliente:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleRemove = useCallback(async (id) => {
    if (!id || loadingRemoveId === id) return;

    try {
      setLoadingRemoveId(id);
      await deleteDepositoCliente(id);
      notification.success('Cliente removido com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues(emptyDepositoClienteForm);
      }
      reload();
    } catch (error) {
      notification.error('Erro ao remover cliente');
      console.error('Erro ao remover cliente:', error);
    } finally {
      setLoadingRemoveId(null);
    }
  }, [editingId, loadingRemoveId, reload]);

  const handleClear = useCallback(() => {
    setEditingId(null);
    setFormInitialValues(emptyDepositoClienteForm);
    nomeClienteLookupRef.current = '';
  }, []);

  const handleBuscarNomeCliente = useCallback(async (rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue : rawValue?.target?.value;
    const cnpj = toDigitsOnly(value || '');
    if (!cnpj || cnpj.length !== 14) return;
    if (nomeClienteLookupRef.current === cnpj) return;

    try {
      const response = await getNomeClientePorCnpj(cnpj);
      const nomeCliente = response?.nomeCliente || response?.data?.nomeCliente || '';

      if (nomeCliente) {
        formRef.current?.change('nomeCliente', nomeCliente);
        nomeClienteLookupRef.current = cnpj;
      }
    } catch (error) {
      console.error('Erro ao buscar nome do cliente:', error);
    }
  }, []);

  const formStructureWithLookup = useMemo(
    () =>
      depositoClienteFormStructure.map((row) =>
        row.map((field) => {
          if (field?.name === 'cnpjCliente') {
            return {
              ...field,
              onBlur: handleBuscarNomeCliente
            };
          }
          return field;
        })
      ),
    [handleBuscarNomeCliente]
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSaving(true);
        const payload = buildPayload(values);
        if (editingId) {
          await updateDepositoCliente(editingId, payload);
          notification.success('Cliente atualizado com sucesso!');
        } else {
          await createDepositoCliente(payload);
          notification.success('Cliente criado com sucesso!');
        }
        handleClear();
        reload();
      } catch (error) {
        notification.error(error?.response?.data?.error || 'Erro ao salvar cliente');
        console.error('Erro ao salvar cliente:', error);
      } finally {
        setSaving(false);
      }
    },
    [editingId, handleClear, reload]
  );

  const columns = useMemo(
    () => getDepositoClienteColumns(handleEdit, handleRemove, loadingEditId, loadingRemoveId),
    [handleEdit, handleRemove, loadingEditId, loadingRemoveId]
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Clientes - Deposito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FilterPanel
          filters={depositoClienteFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`deposito-clientes-${paginaAtual}-${quantidadePorPagina}`}
          columns={columns}
          data={clientes}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
        />

        <div className="mt-4">
          <div className="mb-3 p-3 border rounded bg-light">
            <strong>Tags disponiveis</strong>
            <div className="mt-2">
              <div><strong>Cabecalho:</strong> #NOME_CLIENTE, #DIA, #MES, #NOME_MES, #ANO, #VALOR_UN_PALETIZACAO, #VALOR_UN_ETIQUETA, #VALOR_DIA_ARMAZENAGEM</div>
              <div className="mt-1"><strong>Itens por NF:</strong> #NF, #QTD_ETQ_NF, #QTD_PALETE_NF, #QTD_CX_NF, #QTD_DIAS_ARM_NF, #QTD_DIAS_EXCEDIDOS, #VALOR_CALCULADO_PALETIZACAO, #VALOR_CALCULADO_ETIQUETAGEM, #VALOR_CALCULADO_ARMAZENAGEM</div>
            </div>
          </div>

          <Form
            key={editingId || 'novo'}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validate={validateDepositoClienteForm}
          >
            {({ handleSubmit, form }) => {
              formRef.current = form;

              return (
                <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm
                    structure={formStructureWithLookup}
                    sections={depositoClienteFormSections()}
                    readOnly={false}
                  />

                  <FormButtonToolbar className="mt-3">
                    <Button variant="secondary" type="button" onClick={handleClear} disabled={saving}>
                      Limpar
                    </Button>
                    <Button
                      variant="outline-primary"
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        const token = generateTrackingToken();
                        form.change('tokenRastreamento', token);
                        form.change('tokenRastreamentoAtivo', true);
                        notification.success('Token gerado com sucesso!');
                      }}
                    >
                      Gerar token
                    </Button>
                    <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                      Salvar
                    </ButtonWithLoading>
                  </FormButtonToolbar>
                </FormContainer>
              );
            }}
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DepositoClientesModal;
